import { useEffect, useMemo, useRef, useState } from 'react'
import { ComposableMap, Geographies, Geography, ZoomableGroup } from 'react-simple-maps'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faMinus, faLocationCrosshairs } from '@fortawesome/free-solid-svg-icons'
import { WORLD, HIGHLIGHT_IDS, projection, MAP_WIDTH, MAP_HEIGHT } from './geoData'
import { coordLngLat, progress, localityName, routeKey } from './tripUtils'
import { buildTerritoryShapes, fitView, territoryOfTrip } from './territoryUtils'
import { TerritoryBands, TerritoryLabels } from './TerritoryLayer'
import ConquestPanel from './ConquestPanel'
import { TERRITORIES } from './territories'

const project = lngLat => {
  const p = projection(lngLat)
  return p && Number.isFinite(p[0]) && Number.isFinite(p[1]) ? p : null
}

// Vista inicial: encuadra todos los territorios (NOA, Cuyo, Litoral, Costa y el Este).
const HOME = fitView(buildTerritoryShapes(TERRITORIES)) || { coordinates: [-57.6, -33.4], zoom: 1.85 }
// Ancho que necesita el panel de conquista abierto sin tapar el mapa.
const PANEL_ROOM_PX = 310
const CARS_PER_ROUTE_CAP = 6

const buildArc = (p0, p2, bend = 0.2) => {
  const [x0, y0] = p0
  const [x2, y2] = p2
  const mx = (x0 + x2) / 2
  const my = (y0 + y2) / 2
  const dx = x2 - x0
  const dy = y2 - y0
  const len = Math.hypot(dx, dy) || 1
  const cx = mx - (dy / len) * len * bend
  const cy = my + (dx / len) * len * bend
  return { d: `M${x0},${y0} Q${cx},${cy} ${x2},${y2}`, c: [cx, cy], len }
}

const quadAt = (p0, c, p2, t) => {
  const u = 1 - t
  return [u * u * p0[0] + 2 * u * t * c[0] + t * t * p2[0], u * u * p0[1] + 2 * u * t * c[1] + t * t * p2[1]]
}
const quadAngle = (p0, c, p2, t) => {
  const dx = 2 * (1 - t) * (c[0] - p0[0]) + 2 * t * (p2[0] - c[0])
  const dy = 2 * (1 - t) * (c[1] - p0[1]) + 2 * t * (p2[1] - c[1])
  return (Math.atan2(dy, dx) * 180) / Math.PI
}

const CAR_SCALE = 1.5
const CarMarker = ({ selected }) => (
  <g>
    <ellipse cx={0} cy={5} rx={16} ry={7} fill='#000' opacity={0.28} />
    {selected && <circle r={22} className='live-car-halo' fill='none' stroke='var(--coral)' strokeWidth={2.4} />}
    <rect x={-13} y={-7.5} width={26} height={15} rx={6.5} fill='url(#live-car-body)' stroke='#7a2d18' strokeWidth={1} />
    <rect x={3.2} y={-5} width={6.6} height={10} rx={2.2} fill='#26160f' />
    <rect x={-9.6} y={-5} width={6} height={10} rx={2.2} fill='#2c1a10' />
    <rect x={-3} y={-5.4} width={6} height={10.8} rx={2} fill='#ffd9b0' />
    <circle cx={11.5} cy={-4} r={1.5} fill='#fff2d6' />
    <circle cx={11.5} cy={4} r={1.5} fill='#fff2d6' />
    <circle cx={-12} cy={-4} r={1.3} fill='#7a1d10' />
    <circle cx={-12} cy={4} r={1.3} fill='#7a1d10' />
  </g>
)

const LiveMap = ({ liveTrips = [], upcomingTrips = [], showUpcoming = false, now = Date.now(), isLive = true, selectedId = null, onOpenTrip, territories = null }) => {
  const [position, setPosition] = useState(HOME)
  const [showTerritories, setShowTerritories] = useState(true)
  const [selectedTerritory, setSelectedTerritory] = useState(null)
  const wrapRef = useRef(null)
  const [panelRoomy, setPanelRoomy] = useState(true)

  // El SVG es vertical, así que en pantallas anchas sobra agua a los costados.
  // El panel arranca abierto sólo si entra ahí.
  useEffect(() => {
    const el = wrapRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect
      setPanelRoomy((width - height * (MAP_WIDTH / MAP_HEIGHT)) / 2 >= PANEL_ROOM_PX)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const shapes = useMemo(() => buildTerritoryShapes(territories?.territories), [territories])

  // Viajes en curso por territorio (para el "en viaje" del panel y el pulso en el mapa).
  const liveByTerritory = useMemo(() => {
    const counts = {}
    if (!territories) return counts
    liveTrips.forEach(trip => {
      const key = territoryOfTrip(trip, territories.territories)
      if (key) counts[key] = (counts[key] || 0) + 1
    })
    return counts
  }, [liveTrips, territories])
  const activeTerritories = useMemo(() => new Set(Object.keys(liveByTerritory)), [liveByTerritory])

  const focusTerritory = key => {
    if (selectedTerritory === key) {
      setSelectedTerritory(null)
      setPosition(HOME)
      return
    }
    const shape = shapes.find(s => s.key === key)
    const view = shape && fitView([shape], { pad: 60, maxZoom: 5 })
    setSelectedTerritory(key)
    if (view) setPosition(view)
  }

  // Agrupar viajes en curso por ruta (origen→destino) para mostrar varios autos.
  const routes = useMemo(() => {
    const groups = new Map()
    liveTrips.forEach(trip => {
      const o = coordLngLat(trip?.Journey?.origin)
      const d = coordLngLat(trip?.Journey?.destination)
      if (!o || !d) return
      const p0 = project(o)
      const p2 = project(d)
      if (!p0 || !p2) return
      const key = routeKey(trip)
      if (!groups.has(key)) {
        const arc = buildArc(p0, p2)
        groups.set(key, {
          key,
          d: arc.d,
          p0,
          p2,
          c: arc.c,
          dur: Math.min(34, Math.max(11, arc.len / 12)),
          destName: localityName(trip?.Journey?.destination_name),
          trips: []
        })
      }
      groups.get(key).trips.push(trip)
    })
    return Array.from(groups.values())
  }, [liveTrips])

  const upcomingPoints = useMemo(() => {
    if (!showUpcoming) return []
    const map = new Map()
    upcomingTrips.forEach(t => {
      const c = coordLngLat(t?.Journey?.origin)
      if (!c) return
      const p = project(c)
      if (!p) return
      const key = `${p[0].toFixed(1)},${p[1].toFixed(1)}`
      if (!map.has(key)) map.set(key, { p, name: localityName(t?.Journey?.origin_name), count: 0 })
      map.get(key).count += 1
    })
    return Array.from(map.values())
  }, [upcomingTrips, showUpcoming])

  const zoom = position.zoom
  const k = 1 / zoom
  const clamp = z => Math.min(8, Math.max(1, z))
  const zoomBy = factor => setPosition(p => ({ coordinates: p.coordinates, zoom: clamp(p.zoom * factor) }))
  const reset = () => {
    setSelectedTerritory(null)
    setPosition(HOME)
  }
  const territoriesOn = showTerritories && shapes.length > 0

  const btn =
    'w-9 h-9 flex items-center justify-center rounded-xl border transition-all duration-200 shadow-sm'
  const btnStyle = { background: 'var(--panel)', borderColor: 'var(--border)', color: 'var(--text-soft)' }

  return (
    <div ref={wrapRef} className='relative w-full h-full'>
      <ComposableMap projection={projection} width={MAP_WIDTH} height={MAP_HEIGHT} style={{ width: '100%', height: '100%' }}>
        <defs>
          <linearGradient id='live-arc' x1='0%' y1='0%' x2='100%' y2='0%'>
            <stop offset='0%' stopColor='var(--amber)' />
            <stop offset='100%' stopColor='var(--coral)' />
          </linearGradient>
          <linearGradient id='live-car-body' x1='0%' y1='0%' x2='100%' y2='0%'>
            <stop offset='0%' stopColor='#f7b13e' />
            <stop offset='100%' stopColor='#f2622f' />
          </linearGradient>
        </defs>

        <rect x={0} y={0} width={MAP_WIDTH} height={MAP_HEIGHT} style={{ fill: 'var(--map-water)' }} />

        <ZoomableGroup center={position.coordinates} zoom={position.zoom} minZoom={1} maxZoom={8} onMoveEnd={setPosition}>
          <Geographies geography={WORLD}>
            {({ geographies }) =>
              geographies.map(geo => {
                const hi = HIGHLIGHT_IDS.includes(geo.id)
                return (
                  <Geography
                    key={geo.rsmKey}
                    geography={geo}
                    style={{
                      default: { fill: hi ? 'var(--map-land-hi)' : 'var(--map-land)', stroke: hi ? 'var(--map-stroke-hi)' : 'var(--map-stroke)', strokeWidth: hi ? 0.8 : 0.4, outline: 'none' },
                      hover: { fill: hi ? 'var(--map-land-hi)' : 'var(--map-land)', outline: 'none' },
                      pressed: { outline: 'none' }
                    }}
                  />
                )
              })
            }
          </Geographies>

          {/* Territorios conquistados alrededor de cada ruta */}
          {territoriesOn && (
            <TerritoryBands shapes={shapes} k={k} activeKeys={activeTerritories} selectedKey={selectedTerritory} onSelect={focusTerritory} />
          )}

          {/* Orígenes de próximos viajes (toggle) */}
          {upcomingPoints.map((u, i) => (
            <g key={`up-${i}`} transform={`translate(${u.p[0]}, ${u.p[1]})`}>
              <circle r={6 * k} fill='var(--amber)' fillOpacity={0.18} />
              <circle r={2.6 * k} fill='var(--amber)' stroke='var(--map-label-halo)' strokeWidth={0.8 * k} />
            </g>
          ))}

          {/* Rutas */}
          {routes.map(r => (
            <g key={`route-${r.key}`}>
              <path id={`path-${r.key}`} d={r.d} fill='none' style={{ stroke: 'var(--map-route)' }} strokeWidth={3.4} strokeLinecap='round' />
              <path d={r.d} fill='none' stroke='url(#live-arc)' strokeOpacity={0.7} strokeWidth={1.8} strokeLinecap='round' className='live-flow' />
              {/* origen / destino sutiles */}
              <circle cx={r.p0[0]} cy={r.p0[1]} r={2.6 * k + 0.5} fill='var(--amber)' />
              <circle cx={r.p2[0]} cy={r.p2[1]} r={3 * k + 0.5} fill='var(--coral)' stroke='var(--map-label-halo)' strokeWidth={1 * k} />
              <text
                x={r.p2[0] + 6 * k}
                y={r.p2[1] + 3 * k}
                style={{ fill: 'var(--map-label)', paintOrder: 'stroke', stroke: 'var(--map-label-halo)', strokeWidth: 2.6 * k }}
                fontSize={9 * k}
                fontWeight={700}
              >
                {r.destName}
              </text>
            </g>
          ))}

          {territoriesOn && <TerritoryLabels shapes={shapes} k={k} selectedKey={selectedTerritory} onSelect={focusTerritory} />}

          {/* Autos: uno por viaje, varios por ruta. Vivo = animado; histórico = quieto. */}
          {routes.map(r => {
            const cars = r.trips.slice(0, CARS_PER_ROUTE_CAP)
            const overflow = r.trips.length - cars.length
            return (
              <g key={`cars-${r.key}`}>
                {cars.map((trip, i) => {
                  const sel = selectedId === trip._id
                  const open = e => { e.stopPropagation(); onOpenTrip && onOpenTrip(trip._id) }
                  if (isLive) {
                    return (
                      <g key={trip._id} style={{ cursor: 'pointer' }} onClick={open}>
                        <g>
                          <animateMotion dur={`${r.dur}s`} repeatCount='indefinite' rotate='auto' begin={`-${((i + 0.4) * r.dur) / Math.max(cars.length, 1)}s`}>
                            <mpath href={`#path-${r.key}`} />
                          </animateMotion>
                          <g transform={`scale(${k * CAR_SCALE})`}>
                            <CarMarker selected={sel} />
                          </g>
                        </g>
                      </g>
                    )
                  }
                  const t = progress(trip, now)
                  const pos = quadAt(r.p0, r.c, r.p2, t)
                  const ang = quadAngle(r.p0, r.c, r.p2, t)
                  return (
                    <g key={trip._id} style={{ cursor: 'pointer' }} onClick={open} transform={`translate(${pos[0]}, ${pos[1]}) rotate(${ang}) scale(${k * CAR_SCALE})`}>
                      <CarMarker selected={sel} />
                    </g>
                  )
                })}
                {overflow > 0 && (
                  <g transform={`translate(${quadAt(r.p0, r.c, r.p2, 0.5)[0]}, ${quadAt(r.p0, r.c, r.p2, 0.5)[1]})`}>
                    <circle r={8 * k} fill='var(--coral)' />
                    <text textAnchor='middle' dy={3 * k} fontSize={8 * k} fontWeight={700} fill='#fff'>+{overflow}</text>
                  </g>
                )}
              </g>
            )
          })}
        </ZoomableGroup>
      </ComposableMap>

      {territories && shapes.length > 0 && (
        <ConquestPanel
          data={territories}
          liveByKey={liveByTerritory}
          visible={showTerritories}
          setVisible={setShowTerritories}
          selectedKey={selectedTerritory}
          onSelect={focusTerritory}
          defaultOpen={panelRoomy}
        />
      )}

      <div className='absolute bottom-3 right-3 flex flex-col gap-2'>
        <button className={btn} style={btnStyle} onClick={() => zoomBy(1.6)} title='Acercar'><FontAwesomeIcon icon={faPlus} className='text-sm' /></button>
        <button className={btn} style={btnStyle} onClick={() => zoomBy(1 / 1.6)} title='Alejar'><FontAwesomeIcon icon={faMinus} className='text-sm' /></button>
        <button className={btn} style={btnStyle} onClick={reset} title='Centrar'><FontAwesomeIcon icon={faLocationCrosshairs} className='text-sm' /></button>
      </div>
      <div className='absolute bottom-3 left-3 text-[10px] select-none' style={{ color: 'var(--text-faint)' }}>
        Rueda para zoom · arrastrá para mover · tocá un auto para ver el viaje o una ruta para enfocarla
      </div>
    </div>
  )
}

export default LiveMap
